import {NgClass} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {
  FormControl,
  FormGroup,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {validators} from '@emma-labs/shared/angular';
import {ErrorResponse, ErrorResponseCode, PASSWORD_TOKEN_PARAM_KEY} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ButtonDirective} from 'primeng/button';
import {PasswordModule} from 'primeng/password';
import {Ripple} from 'primeng/ripple';
import {first} from 'rxjs';
import {BrokerProfileService} from '../../../services/broker-profile/broker-profile.service';
import {BTN_LOADING_ANIMATION_TIME} from '../../../utils/utility-constants';
import {PasswordUpdateRequest} from '../../profile-page/password/password-update-request';
import {AuthBackgroundComponent} from '../auth-background/auth-background.component';

interface PasswordResetForm {
  newPassword: FormControl<string>;
  confirmNewPassword: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'em-password-reset-page',
  templateUrl: './password-reset-page.component.html',
  imports: [ReactiveFormsModule, PasswordModule, NgClass, ButtonDirective, AuthBackgroundComponent, Ripple],
})
export class PasswordResetPageComponent implements OnInit {
  form: FormGroup<PasswordResetForm>;
  isSuccess = false;
  isButtonLoading = false;
  isPasswordTooWeakError = false;
  decodedToken = '';

  constructor(
    private readonly router: Router,
    private readonly profileService: BrokerProfileService,
    private readonly fb: NonNullableFormBuilder,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.form = this.createForm();
  }

  ngOnInit(): void {
    const token = this.activatedRoute.snapshot.queryParams[PASSWORD_TOKEN_PARAM_KEY] ?? '';
    this.decodedToken = decodeURIComponent(token);
  }

  hasPasswordError(errorCode: string): boolean {
    return (
      this.form.controls.newPassword.hasError(errorCode) ||
      this.form.controls.newPassword.hasError('required')
    );
  }

  goToLoginPage(): void {
    this.router.navigate(['/', 'auth']);
  }

  submit(): void {
    this.isButtonLoading = true;

    const passwordUpdateRequest: PasswordUpdateRequest = {
      newPassword: this.form.controls.newPassword.value,
      passwordToken: this.decodedToken,
    };

    this.profileService
      .updatePasswordWithToken(passwordUpdateRequest)
      .pipe(first())
      .subscribe({
        next: () => {
          setTimeout(() => {
            this.isSuccess = true;
            this.isButtonLoading = false;
          }, BTN_LOADING_ANIMATION_TIME);
        },
        error: (error: ErrorResponse) => {
          setTimeout(() => {
            switch (error.code) {
              case ErrorResponseCode.INVALID_TOKEN:
                this.router.navigate(['/', 'auth'], {queryParams: {invalidPasswordToken: true}});
                break;
              case ErrorResponseCode.PASSWORD_TOO_WEAK:
                this.isPasswordTooWeakError = true;
                break;
            }
            this.isButtonLoading = false;
          }, BTN_LOADING_ANIMATION_TIME);
        },
      });
  }

  private createForm(): FormGroup<PasswordResetForm> {
    const form = this.fb.group(
      {
        newPassword: ['', [Validators.required, validators.password]],
        confirmNewPassword: ['', [Validators.required, validators.password]],
      },
      {
        validators: [validators.passwordConfirmation],
      },
    );

    form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.isPasswordTooWeakError = false;
    });

    return form;
  }
}
